<template>
  <div class="container" v-if="tableData.length > 0">
    <div v-if="automationsAdvancedView">
      <b-table
          key="advanced"
          :striped="true"
          :narowed="true"
          :data="tableData"
          :detailed="true"
          detail-key="id"
      >
        <template slot-scope="props">
          <b-table-column field="num" numeric>{{props.row.num}}</b-table-column>
          <b-table-column label="Name" field="truncatedName">
            <router-link :to="{path: '/configure/automations/' + props.row.id}">{{props.row.truncatedName}}
            </router-link>
          </b-table-column>
          <b-table-column label="Last Run" field="lastRun" centered>{{props.row.lastRun}}</b-table-column>
          <b-table-column label="Next Run" field="nextRun" centered>{{props.row.nextRun}}</b-table-column>
          <b-table-column label="Enabled" field="enabled" centered>{{props.row.enabled === 1 ? 'Yes' : 'No'}}
          </b-table-column>
          <b-table-column label="Run" numeric>
            <b-button type="is-primary" class="is-small" :disabled="!props.row.enabled"
                      @click="handleRun(props.row.id)">Run
            </b-button>
          </b-table-column>
        </template>

        <template slot="detail" slot-scope="props">
          <div>
            <div>Name: {{props.row.name}}</div>
            <div v-if="props.row.hasEvents">Events: {{props.row.stringifiedEvents}}</div>
            <div v-if="props.row.hasSchedules">Schedules: {{props.row.stringifiedSchedules}}</div>
            <div v-if="props.row.hasConditions">Conditions: {{props.row.stringifiedConditions}}</div>
            <div v-if="props.row.hasActions">Actions: {{props.row.stringifiedActions}}</div>
            <div class="buttons" style="margin-top: 1.25rem;">
              <b-field grouped>
                <b-field label="Run delayed:" label-position="on-border">
                  <b-select v-model="runDelay" size="is-small">
                    <option v-for="opt in optionsForRunDelayed" :key="opt.id" :value="opt.id">{{opt.name}}</option>
                  </b-select>

                  <b-button type="is-success" class="is-small" label="Run delayed"
                            @click="handleRunDelayed(props.row.id)" style="margin-left: 0.5rem;"/>
                </b-field>
              </b-field>
            </div>
          </div>
        </template>
      </b-table>
    </div>
    <div v-else>
      <b-table
          key="simple"
          :striped="true"
          :narowed="true"
          :data="tableData"
          :mobile-cards="false"
      >
        <template slot-scope="p">
          <b-table-column label="Name" field="truncatedName">
            <router-link :to="{path: '/configure/automations/' + p.row.id}">{{p.row.truncatedName}}</router-link>
          </b-table-column>
          <b-table-column numeric>
            <b-button type="is-primary" class="is-small" :disabled="!p.row.enabled" @click="handleRun(p.row.id)">Run
            </b-button>
          </b-table-column>
        </template>
      </b-table>
    </div>
  </div>
  <div class="container" v-else>No automations.</div>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex';
  import BTable from "buefy/src/components/table/Table";
  import BTableColumn from "buefy/src/components/table/TableColumn";
  import BButton from "buefy/src/components/button/Button";
  import {ToastProgrammatic as Toast} from 'buefy';
  import {formatTime} from '../../utils/utils';
  import BField from "buefy/src/components/field/Field";
  import BSelect from "buefy/src/components/select/Select";

  export default {
    components: {
      BSelect,
      BField,
      BButton,
      BTableColumn,
      BTable,
    },
    name: "automation-list",
    data() {
      return {
        runDelay: 0
      };
    },
    computed: {
      ...mapGetters('automations', ['automations']),
      ...mapGetters('ui', ['automationsAdvancedView', 'automationRunDelayOptions']),
      tableData() {
        let num = 0;
        return this.automations.map(item => {
          num++;
          const truncatedName = item.name;
          const { events, schedules, actions, conditions } = item.definition;
          const displayedConditions = conditions ? Object.keys(conditions).length : 0;
          const hftLastRun = item['last_run'] > 0 ? formatTime(item['last_run']) : '-';
          const hftNextRun = item['next_run'] > 0 ? formatTime(item['next_run']) : '-';
          return {
            ...item,
            num,
            truncatedName,
            lastRun: hftLastRun,
            nextRun: hftNextRun,
            hasEvents: events.length > 0,
            hasSchedules: schedules.length > 0,
            hasActions: actions.length > 0,
            hasConditions: conditions,
            displayedConditions,
            stringifiedEvents: JSON.stringify(events),
            stringifiedSchedules: JSON.stringify(schedules),
            stringifiedConditions: JSON.stringify(conditions),
            stringifiedActions: JSON.stringify(actions),
            enabled: parseInt(item.disabled) === 1 ? 0 : 1
          };
        });
      },
      optionsForRunDelayed() {
        const ret = [
          {
            id: 0,
            name: 'no delay'
          }
        ];

        if (this.automationRunDelayOptions) {
          this.automationRunDelayOptions.forEach(delay => {
            if (delay > 0) {
              const minutes = delay / 60;
              const plural = minutes > 0 ? 'minutes' : 'minute';

              ret.push({
                id: delay,
                name: `${minutes} ${plural}`
              });
            }
          });
        }

        return ret;
      }
    },
    methods: {
      ...mapActions('automations', ['testAutomation', 'runAutomation', 'delayAutomation']),
      handleClickEdit(id) {
        this.$router.push('/configure/automations/' + id);
      },
      handleClickTest(id) {
        this.testAutomation({
          id: id,
          callback: data => {
            const { status, message } = data;
            const type = parseInt(status) === 0 ? 'is-success' : 'is-danger';
            Toast.open({ message: message, type: type, duration: 3000 });
          }
        });
      },
      handleRun(id) {
        this.runAutomation({
          id: id,
          callback: data => {
            const { status, message } = data;
            const type = parseInt(status) === 0 ? 'is-success' : 'is-danger';
            Toast.open({ message: message, type: type, duration: 3000 });
          }
        });
      },
      handleRunDelayed(id) {
        this.delayAutomation({
          id: id,
          delay: this.runDelay,
          callback: data => {
            const { status, message } = data;
            const type = parseInt(status) === 0 ? 'is-success' : 'is-danger';
            Toast.open({ message: message, type: type, duration: 3000 });
          }
        })
      }
    },
    mounted() {
    }
  }
</script>

<style scoped>

</style>